// SketchUploadService.js
//
// Upload sketch thumbnails and canvas images to Firebase Storage.

import { ref, uploadBytes } from 'firebase/storage';

import { storage } from 'firebaseConfig';
import { assertBaseUploadAllowed } from 'services/bases/BaseUploadPolicyService';
import { getSignedUrlForStoragePath } from 'services/storage/StorageAccessService';
import { prepareBaseUploadIntent } from 'services/storage/StorageUploadIntentService';
import { dataUrlToBlob } from 'services/storage/SignatureUploadService';

const OUTPUT_MIME_TYPE = 'image/png';

function cleanString(value) {
  return String(value || '').trim();
}

function normalizeStoragePath(value) {
  return cleanString(value).replace(/^\/+/, '');
}

function sketchPrefix(nodeKey) {
  return `nodes/${cleanString(nodeKey)}/sketches/`;
}

function nodeKeyFromSketchPath(storagePath) {
  const match = normalizeStoragePath(storagePath).match(/^nodes\/([^/]+)\//);
  return match ? cleanString(match[1]) : '';
}

/**
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.nodeKey
 * @param {string} options.storagePath
 * @param {Blob} options.blob
 * @param {string} [options.contentType]
 * @param {string} [options.uploadKind]
 * @returns {Promise<string>}
 */
async function uploadSketchBlob({
  baseKey,
  nodeKey,
  storagePath,
  blob,
  contentType = OUTPUT_MIME_TYPE,
  uploadKind = 'sketch',
}) {
  const cleanedBaseKey = cleanString(baseKey);
  const cleanedNodeKey = cleanString(nodeKey);
  const cleanedPath = normalizeStoragePath(storagePath);
  if (!cleanedBaseKey || !cleanedNodeKey || !cleanedPath) {
    throw new Error('Base, node, and storage path are required to upload a sketch.');
  }
  if (!blob?.size) {
    throw new Error('Sketch image is empty.');
  }

  await assertBaseUploadAllowed({
    baseKey: cleanedBaseKey,
    fileName: cleanedPath.split('/').pop() || 'sketch.png',
    fileSizeBytes: blob.size,
  });

  const uploadIntent = await prepareBaseUploadIntent({
    baseKey: cleanedBaseKey,
    storagePath: cleanedPath,
    fileSizeBytes: blob.size,
    contentType,
    uploadKind,
    nodeKey: cleanedNodeKey,
    itemKey: cleanedNodeKey,
  });
  const intentId = cleanString(uploadIntent?.intentId);

  await uploadBytes(ref(storage, cleanedPath), blob, {
    cacheControl: 'public, max-age=3600',
    contentType,
    customMetadata: {
      baseKey: cleanedBaseKey,
      ...(intentId ? { intentId } : {}),
      nodeKey: cleanedNodeKey,
    },
  });

  return cleanedPath;
}

/**
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.nodeKey
 * @param {Blob} options.blob
 * @returns {Promise<{ storagePath: string, downloadUrl: string }>}
 */
export async function uploadSketchThumbnail({ baseKey, nodeKey, blob }) {
  const storagePath = `${sketchPrefix(nodeKey)}thumbnail.png`;
  await uploadSketchBlob({
    baseKey,
    blob,
    nodeKey,
    storagePath,
    uploadKind: 'sketch-thumbnail',
  });
  return { downloadUrl: storagePath, storagePath };
}

/**
 * Uploads in-memory Excalidraw files and returns Firestore-safe metadata.
 *
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.nodeKey
 * @param {object} [options.files]
 * @returns {Promise<object>}
 */
export async function persistSketchBinaryFiles({ baseKey, nodeKey, files = {} }) {
  const next = {};
  const entries = Object.entries(files || {});
  for (let index = 0; index < entries.length; index += 1) {
    const [fileId, file] = entries[index];
    const id = cleanString(fileId);
    if (!id || !file || typeof file !== 'object') continue;

    const existingPath = normalizeStoragePath(file.storagePath);
    if (existingPath && !file.dataURL) {
      next[id] = {
        mimeType: cleanString(file.mimeType) || OUTPUT_MIME_TYPE,
        storagePath: existingPath,
      };
      continue;
    }

    const dataUrl = cleanString(file.dataURL);
    if (!dataUrl) continue;

    const blob = dataUrlToBlob(dataUrl);
    const mimeType = cleanString(file.mimeType || blob.type) || OUTPUT_MIME_TYPE;
    const storagePath = `${sketchPrefix(nodeKey)}files/${id}`;
    await uploadSketchBlob({
      baseKey,
      blob,
      contentType: mimeType,
      nodeKey,
      storagePath,
      uploadKind: 'sketch-file',
    });
    next[id] = { mimeType, storagePath };
  }
  return next;
}

/**
 * @param {string} dataUrl
 * @returns {Promise<string>}
 */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read sketch file.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Loads stored sketch files into Excalidraw BinaryFiles (dataURL).
 *
 * @param {object} [fileMeta]
 * @returns {Promise<object>}
 */
export async function hydrateSketchBinaryFiles(fileMeta = {}) {
  const next = {};
  const entries = Object.entries(fileMeta || {});
  for (let index = 0; index < entries.length; index += 1) {
    const [fileId, entry] = entries[index];
    const id = cleanString(fileId);
    const storagePath = normalizeStoragePath(entry?.storagePath);
    if (!id || !storagePath) continue;

    const downloadUrl = await getSignedUrlForStoragePath({
      nodeKey: cleanString(entry?.nodeKey) || nodeKeyFromSketchPath(storagePath),
      storagePath,
    });
    if (!downloadUrl) {
      throw new Error('Failed to load a sketch image.');
    }
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error('Failed to load a sketch image.');
    }
    const blob = await response.blob();
    next[id] = {
      created: Date.now(),
      dataURL: await blobToDataUrl(blob),
      id,
      mimeType: cleanString(entry?.mimeType || blob.type) || OUTPUT_MIME_TYPE,
    };
  }
  return next;
}
